import {
  getRandom,
  preventEventForKeys,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  Box,
  DatePicker,
  FieldTitle,
  LabraToolTip,
  labraUIDateFormat,
  PlanType,
  TextInput,
} from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import { useStyles } from './Details.styles'
import { getMinMaxDateAwsOffer } from '../../../modules/privateOffer/privateOfferCreation/utils/dateHelpers'
import type {
  KeyValue,
  OffersWithErrors,
} from '../../../modules/privateOffer/privateOfferCreation/types'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { DateTime } from 'luxon'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { useCommonStyles } from '../../../common/styles/common.styles'
import { AddRecipients } from '../Recipients'
import {
  getStartDateTypeValue,
  getStartDateTypeValueToDisplay,
} from '../CustomerTerm/utils'
import { planTypesForCommonFields } from '../PricingComponent/utils'
import { specifiedDateField, startAwsDateTypeField } from './fields'
import { ExpirationDate } from './ExpirationDate'
import { flyoutFormDebounceTime, getRecipientErrMapping } from './utils'

export type DetailsProps = {
  privateOfferWithError: OffersWithErrors
  addOfferRecipient: (key: string) => void
  deleteOfferRecipient: (index: number, key: string) => () => void
  updateRecipientsData: (index: number, key: string) => (data: KeyValue) => void
  handleDetailsChange: (data: KeyValue) => void
  planType: PlanType
}

export const Details = React.memo(
  ({
    privateOfferWithError,
    addOfferRecipient,
    deleteOfferRecipient,
    updateRecipientsData,
    handleDetailsChange,
    planType,
  }: DetailsProps) => {
    const { classes } = useStyles()
    const { classes: commonClasses } = useCommonStyles()
    const { errors, ...privateOffer } = privateOfferWithError
    const cloudMarketPlace = useCurrentCloudProvider()
    const [serviceLengthValue, setServiceLengthValue] = useState(
      privateOffer.serviceLength
    )
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    useEffect(() => {
      setRandomKey(getRandom())
    }, [])
    const recipientsError = [
      ...((errors?.offerRecipients || []) as Array<Record<string, unknown>>),
    ]
    const offerRecipientsWithError = useMemo(
      () =>
        getRecipientErrMapping(privateOffer.offerRecipients, recipientsError),
      [privateOffer.offerRecipients, recipientsError]
    )
    const handleClick = () => {
      addOfferRecipient('offerRecipients')
    }
    const deleteRow = (index: number) => () => {
      deleteOfferRecipient(index, 'offerRecipients')()
    }
    const updateValue =
      (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        updateRecipientsData(
          index,
          'offerRecipients'
        )({ key: event.target.id, value: event.target.value })
      }

    const { timeZone } = useTimeZone()
    const { minDate, maxDate } = getMinMaxDateAwsOffer({ timeZone })

    const specifiedDateFieldValue = {
      ...specifiedDateField,
      defaultValue: !isEmpty(privateOfferWithError?.agreementStartDate)
        ? privateOfferWithError?.agreementStartDate
        : null,
      minDate,
      maxDate,
    }

    const startDateTypeFieldValue = {
      ...startAwsDateTypeField,
      value: getStartDateTypeValueToDisplay(
        privateOfferWithError?.variableStartDate
      ),
    }

    const updateAgreementStartDate = async () => {
      await handleDetailsChange({
        key: 'agreementStartDate',
        value: '',
      })
    }

    const handleInputChangeSelect = useCallback(async (event: unknown) => {
      const { name, value } = (event as React.ChangeEvent<HTMLInputElement>)
        .target
      const actualValue = getStartDateTypeValue(value)
      await handleDetailsChange({
        key: name as string,
        value: actualValue,
      })
      setTimeout(async () => {
        await updateAgreementStartDate()
      }, flyoutFormDebounceTime)
    }, [])

    const handleStartDateChange = useCallback(
      (date: string | null) => {
        const updatedDate = date
          ? DateTime.fromISO(date).toFormat('yyyy-MM-dd').toLocaleString()
          : ''
        handleDetailsChange({
          key: 'agreementStartDate',
          value: updatedDate,
        })
      },
      [privateOfferWithError.agreementStartDate]
    )

    const handleUpdateServiceLength = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const serviceLengthData =
        event.target.value !== ''
          ? Math.round(Number(event.target.value))
          : undefined
      setServiceLengthValue(serviceLengthData)
      handleDetailsChange({
        key: event.target.name as string,
        value: serviceLengthData,
      })
    }
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleDetailsChange({
        key: event.target.name as string,
        value:
          event.target.name === 'serviceLength'
            ? (event.target.value as unknown as number)
            : (event.target.value as string),
      })
    }

    const fieldForExpiration: FormField = {
      title: 'Date',
      humanReadableKey: 'Offer expiration date',
      placeHolder: 'Offer expiration date',
      id: 'offerExpirationDate',
      isRequired: true,
      isReadOnly: false,
      isCustom: false,
      defaultValue: !isEmpty(privateOffer.offerExpirationDate)
        ? privateOffer.offerExpirationDate
        : undefined,
      dataType: 'date',
      minDate: 'today',
    }
    const handleDateChange = useCallback(
      (date: string | null) => {
        handleDetailsChange({
          key: 'subscriptionEndDate',
          value: date || '',
        })
      },
      [privateOfferWithError.subscriptionEndDate]
    )
    const field: FormField = {
      title: 'Date',
      humanReadableKey: 'Subscription end date',
      placeHolder: 'Subscription end date',
      id: 'subscriptionEndDate',
      isRequired: true,
      isReadOnly: false,
      isCustom: false,
      defaultValue: !isEmpty(privateOfferWithError?.subscriptionEndDate)
        ? (privateOfferWithError?.subscriptionEndDate as string)
        : undefined,
      dataType: 'date',
      minDate: 'today',
    }

    const shouldShowFieldsForCommonPlanType =
      planTypesForCommonFields.includes(planType)

    return (
      <div>
        <Box heading={'Details'} subHeading={''}>
          <div
            className={clsx(
              classes.subHeadingContainer,
              classes.companyDetailSubHead
            )}
          >
            <Typography
              className={classes.subHeading}
              data-testid="details-subheading"
            >
              Company details
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={'Name of the company to whom offer will be sent'}
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <div className={classes.companyDetailRow}>
            <div className={classes.name} key={`companyName-${randomKey}`}>
              <TextInput
                isRequired
                keyName="companyName"
                title="Name"
                value={privateOffer?.companyName || ''}
                onChange={handleChange}
                isError={!isEmpty(errors?.companyName)}
                errorText={(errors?.companyName as string) || ''}
                isReadOnly={false}
                type="string"
                placeHolder=""
                variant="regularV2"
              />
            </div>
            <div
              className={classes.awsAccountId}
              key={`awsAccountId-${randomKey}`}
            >
              {cloudMarketPlace === 'AWS' && (
                <TextInput
                  isRequired
                  keyName="awsAccountId"
                  title="AWS account ID"
                  value={privateOffer.awsAccountId || ''}
                  onChange={handleChange}
                  type="string"
                  isError={!isEmpty(errors?.awsAccountId as string)}
                  errorText={(errors?.awsAccountId as string) || ''}
                  isReadOnly={false}
                  placeHolder=""
                  variant="regularV2"
                />
              )}
              {cloudMarketPlace === 'AZURE' && (
                <TextInput
                  placeHolder=""
                  isError={!isEmpty(errors?.billingAccountId as string)}
                  errorText={(errors?.billingAccountId as string) || ''}
                  isRequired={true}
                  isReadOnly={false}
                  keyName="billingAccountId"
                  onChange={handleChange}
                  type="string"
                  title="Azure billing account ID"
                  value={privateOffer.billingAccountId || ''}
                  variant="regularV2"
                />
              )}
            </div>
          </div>
          {cloudMarketPlace === 'AZURE' && (
            <div className={classes.preparedBy}>
              <TextInput
                placeHolder=""
                isError={!isEmpty(errors?.preparedBy as string)}
                errorText={(errors?.preparedBy as string) || ''}
                isRequired={true}
                isReadOnly={false}
                keyName="preparedBy"
                onChange={handleChange}
                type="email"
                title="Prepared by"
                value={privateOffer.preparedBy || ''}
                variant="regularV2"
              />
            </div>
          )}
          <div
            className={clsx(
              classes.offerRecipientsContainer,
              classes.offerRecipientSubHead
            )}
          >
            <Typography
              className={classes.subHeading}
              data-testid="offer-expiration-date"
            >
              Offer recipients
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={'Recipients to whom the offer should be sent.'}
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <AddRecipients
            offerRecipients={offerRecipientsWithError}
            handleClick={handleClick}
            updateValue={updateValue}
            deleteRow={deleteRow}
            buttonLabel={'Add offer recipient'}
          />
          <div
            className={classes.customEmailMessage}
            key={`customMessage-${randomKey}`}
          >
            <TextField
              required
              id="customMessage"
              name="emailCustomMessage"
              label="Custom email message"
              type="text"
              key="customMessage"
              defaultValue={privateOffer.emailCustomMessage}
              onChange={handleChange}
              className={classes.textField}
              error={!isEmpty(errors?.emailCustomMessage as string)}
              helperText={(errors?.emailCustomMessage as string) || ''}
              fullWidth
              variant="standard"
              slotProps={{
                input: {
                  inputProps: { 'data-testid': 'email-custom-message' },
                },
              }}
            />
          </div>
          <div className={classes.subHeadingContainer}>
            <Typography
              className={classes.subHeading}
              data-testid="details-subheading"
            >
              Offer details
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={'Basic details about the offer'}
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <div className={classes.companyDetailRow}>
            <div
              className={classes.offerName}
              key={`privateOfferName-${randomKey}`}
            >
              <TextField
                required
                id="privateOfferName"
                name="privateOfferName"
                label="Offer name"
                key="privateOfferName"
                defaultValue={privateOffer.privateOfferName}
                onChange={handleChange}
                className={classes.textField}
                error={!isEmpty(errors?.privateOfferName as string)}
                helperText={(errors?.privateOfferName as string) || ''}
                fullWidth
                variant="standard"
                slotProps={{
                  input: {
                    inputProps: { 'data-testid': 'private-offer-name' },
                  },
                }}
              />
            </div>
          </div>
          {cloudMarketPlace === 'AWS' ? (
            <div>
              {planType === PlanType.SAAS_CONTRACT ? (
                <div className={classes.startDateUpperContainer}>
                  <div
                    className={clsx(
                      classes.subHeadingContainer,
                      classes.companyDetailSubHead
                    )}
                  >
                    <Typography
                      className={classes.subHeading}
                      data-testid="start-date-heading"
                    >
                      Start date
                    </Typography>
                    <LabraToolTip
                      tooltipPlacement={'right'}
                      label={
                        'Select accepted date if you want the private offer to start as soon as your customer accepts it or select a specific date in the future.'
                      }
                    >
                      <InfoOutlined className={classes.infoIcon} />
                    </LabraToolTip>
                  </div>

                  <Grid container spacing={4} className={classes.textAlign}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                        xl: 3,
                      }}
                    >
                      <FormControl className={classes.selectFormWidth}>
                        <InputLabel
                          id="start-date-type-label"
                          className={classes.inputLabel}
                          data-testid={'select-label-variableStartDate'}
                          classes={{ shrink: commonClasses.shrink }}
                        >
                          {startDateTypeFieldValue.title}{' '}
                          <FieldTitle title={''} isRequired={true} />
                        </InputLabel>
                        <Select
                          labelId={startDateTypeFieldValue.id}
                          name={startDateTypeFieldValue.id}
                          value={startDateTypeFieldValue?.value}
                          onChange={handleInputChangeSelect}
                          label={startDateTypeFieldValue.title}
                          inputProps={{ 'data-testid': 'input' }}
                          variant="standard"
                          className={classes.selectField}
                          IconComponent={props => (
                            <KeyboardArrowDownIcon
                              {...props}
                              fontSize="small"
                              data-testid="arrow-button"
                            />
                          )}
                          classes={{ icon: classes.icon }}
                          data-testid="start-date-selection"
                        >
                          {(startDateTypeFieldValue.options || []).map(
                            menuOption => (
                              <MenuItem
                                data-testid="menu-item"
                                value={menuOption.value}
                                className={classes.menuRoot}
                                key={menuOption.value}
                              >
                                {menuOption.label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {privateOfferWithError?.variableStartDate === false ? (
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                          lg: 3,
                          xl: 3,
                        }}
                      >
                        <DatePicker
                          format={labraUIDateFormat}
                          field={specifiedDateFieldValue}
                          key={'agreementStartDate'}
                          errorHelperText={
                            (errors?.agreementStartDate as string) || ''
                          }
                          handleDateChange={handleStartDateChange}
                          customClasses={clsx(
                            classes.textField,
                            classes.dateField
                          )}
                        />
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </div>
              ) : (
                <></>
              )}
              <div className={classes.startDateLowerContainer}>
                <ExpirationDate
                  handleUpdateExpiration={handleDetailsChange}
                  errorHelperText={errors.offerExpirationDate as string}
                  field={fieldForExpiration}
                  tooltipLabel={
                    'Buyer needs to accept the offer by the provided date'
                  }
                />
                <div className={classes.serviceLengthContainer}>
                  <div className={classes.subHeadingContainer}>
                    <Typography
                      className={classes.subHeading}
                      data-testid="details-subheading"
                    >
                      {shouldShowFieldsForCommonPlanType
                        ? 'Service length details'
                        : 'Subscription end date'}
                    </Typography>
                    <LabraToolTip
                      tooltipPlacement={'right'}
                      label={
                        shouldShowFieldsForCommonPlanType
                          ? 'How many months the buyer can use the dimensions sold in this offer for'
                          : `The date the private offer subscription will end. This value must be greater than 
                the number of days between offer creation (today) and offer expiration, added to the total 
                contract duration. This offer switches back to the public offer after the specified 
                number of days`
                      }
                    >
                      <InfoOutlined className={classes.infoIcon} />
                    </LabraToolTip>
                  </div>
                  <div
                    className={classes.serviceLength}
                    key={`serviceLengthAndSubscriptionDate-${randomKey}`}
                  >
                    {shouldShowFieldsForCommonPlanType ? (
                      <TextField
                        required
                        id="serviceLength"
                        name="serviceLength"
                        key="serviceLength"
                        label="Service length (in months)"
                        defaultValue={serviceLengthValue}
                        value={serviceLengthValue}
                        onChange={handleUpdateServiceLength}
                        className={classes.textField}
                        onKeyDown={preventEventForKeys(['+', '-', 'e', '.'])}
                        type={'number'}
                        error={!isEmpty(errors?.serviceLength as string)}
                        helperText={(errors?.serviceLength as string) || ''}
                        fullWidth
                        variant="standard"
                        slotProps={{
                          input: {
                            inputProps: { 'data-testid': 'serviceLength' },
                          },
                        }}
                      />
                    ) : (
                      <DatePicker
                        format={labraUIDateFormat}
                        field={field}
                        key={'subscriptionEndDate'}
                        errorHelperText={
                          (errors?.subscriptionEndDate as string) || ''
                        }
                        handleDateChange={handleDateChange}
                        customClasses={clsx(
                          classes.textField,
                          classes.dateField
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </Box>
      </div>
    )
  }
)
